import {
  AccessTime as AccessTimeIcon,
  Add as AddIcon,
  Autorenew as AutoreNewIcon,
  DeleteOutline,
} from '@mui/icons-material';
import {
  Alert,
  Checkbox,
  Grid,
  InputAdornment,
  Switch,
  Typography,
} from '@mui/material';
import { CalendarIcon, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { IAssembly } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { OOLocationEditTimingsEvent } from 'src/utils/google-tag-manager';

import { AssemblyListModal } from '../modal/assemblyListModal/AssemblyListModal';
import { getAssembliesStatus, hasConflictStartTime } from './TimeRow.helper';
import {
  IScheduleWeekdayDaypartRecord,
  TimeRowInterface,
} from './TimeRow.interface';
import {
  AssemblyCard,
  Button,
  Card,
  CardContent,
  CardHeader,
  FlexBox,
  LabelText,
  LinkContainer,
  StartTimeContainer,
  StyledMobileTimePicker,
  StyledWarningIcon,
  TimeRowGrid,
  TooltipWrapper,
  WarningIconContainer,
} from './TimeRow.styled';

const AssemblyName = ({
  assemblyName,
  assemblyId,
}: {
  assemblyName: string;
  assemblyId: number;
}) => {
  return (
    <LinkContainer variant="text">
      <Link to={`${PATHS.ASSEMBLY_DETAILS(assemblyId)}`} target="_blank">
        <CustomTooltip
          placement="bottom"
          title={assemblyName}
          cursor="pointer"
          skipValidateOverflow={true}>
          <Typography variant="body2" color="primary" noWrap>
            {assemblyName}
          </Typography>
        </CustomTooltip>
      </Link>
    </LinkContainer>
  );
};

export const TimeRow = (props: TimeRowInterface) => {
  const {
    scheduledDayparts = [],
    originalScheduledDayparts = [],
    scheduleWeekdayDaypartsRecord = [],
  } = props;

  const { scheduleAssembly, defaultAssembly } = React.useMemo(
    () => getAssembliesStatus(props.daypartRecord.defaultAssembly),
    [props.daypartRecord.defaultAssembly],
  );

  const handleUnschedule = () => {
    // on pressing Unschedule Clear from UI

    props.removeScheduleAssembly &&
      props.removeScheduleAssembly(props.daypartRecord.id);
  };

  const { isViewOnly, isOwnerOperator } = useUserRole();

  const [openAssemblyListModal, setOpenAssemblyListModal] = useState(false);
  const { locationCode = '' } = useParams();
  const [scheduleAssemblySelected, setScheduleAssemblySelected] =
    useState(false);

  const onSelectedAssembly = (
    assembly?: IAssembly | null,
    startDate?: string,
  ) => {
    if (assembly) {
      if (scheduleAssemblySelected) {
        props?.setScheduleAssembly &&
          props?.setScheduleAssembly(
            props.daypartRecord.id,
            assembly,
            startDate,
          );
        setScheduleAssemblySelected(false);
      } else {
        props.setDefaultAssembly &&
          props.setDefaultAssembly(props.daypartRecord.id, assembly);
      }
      setOpenAssemblyListModal(false);
    }
  };

  const toggleDayActiveStatus = (
    checked: boolean,
    weekday_daypart_id: number,
  ) => {
    updateStartTime(
      checked ? props.daypartRecord.default_start_time : null,
      weekday_daypart_id,
    );
  };

  const updateStartTime = (time: string | null, weekday_daypart_id: number) => {
    const updatedWeekdayDaypartsRecord = scheduledDayparts.map((item) => {
      return item.weekday_daypart_id === weekday_daypart_id
        ? {
            ...item,
            start_time: time,
          }
        : item;
    });

    props.handleChangeScheduleDayparts(updatedWeekdayDaypartsRecord);
  };

  const OOLocationEditTimingsEventHandler = (
    day: IScheduleWeekdayDaypartRecord,
  ) => {
    if (isOwnerOperator)
      OOLocationEditTimingsEvent(
        locationCode,
        day?.daypart,
        day?.weekday?.name,
        props?.locationGroup?.channel_name,
      );
  };

  const handleToggleDaypartStatus = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;

    const updatedWeekdayDaypartsRecord = scheduledDayparts.map((item) => {
      const orgScheduledDaypart = originalScheduledDayparts.find(
        ({ id }) => id === item.id,
      );

      return {
        ...item,
        start_time: checked
          ? orgScheduledDaypart?.start_time ||
            props.daypartRecord.default_start_time
          : null,
      };
    });

    props.handleChangeScheduleDayparts(updatedWeekdayDaypartsRecord);
  };

  const hasDayparts = !isEmpty(scheduledDayparts);
  const daypartActive = scheduledDayparts.some((item) => item.start_time);

  const canViewDaypart =
    !isViewOnly && !props.isDefaultLocationGroup && !props.rbStoreIntegration;

  const canViewLocationGroupDaypart =
    isViewOnly || !props.isLocationGroupPage || !props.rbStoreIntegration;

  const getAssemblyModalTitle = () => {
    if (scheduleAssemblySelected) {
      return lang.locationAssemblyModal.schedule(props.daypartRecord.name);
    } else if (
      !!props?.daypartRecord?.defaultAssembly &&
      props.daypartRecord.defaultAssembly.length > 0
    ) {
      return lang.locationAssemblyModal.replace(props.daypartRecord.name);
    } else {
      return lang.locationAssemblyModal.add(props.daypartRecord.name);
    }
  };

  return (
    <>
      <FlexBox mb={2} mt={2}>
        <Typography variant="h6">{props.daypartRecord.name}</Typography>
        {canViewDaypart && canViewLocationGroupDaypart && (
          <>
            {hasDayparts ? (
              <Switch
                checked={daypartActive}
                disabled={!!props.daypartRecord?.daypartDisabled}
                onChange={handleToggleDaypartStatus}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            ) : (
              <TooltipWrapper>
                <CustomTooltip
                  title={lang.alertMessages.viewEditTimings}
                  arrow
                  skipValidateOverflow={true}>
                  <StyledWarningIcon />
                </CustomTooltip>
              </TooltipWrapper>
            )}
          </>
        )}
      </FlexBox>
      <Grid container>
        <TimeRowGrid item md={12}>
          <Grid
            item
            xs={scheduleAssembly ? 6 : 12}
            md={scheduleAssembly ? 6 : 12}>
            <AssemblyCard>
              <CardHeader>
                <Typography variant="subtitle2" fontWeight="bold">
                  {lang.defaultAssembly}
                </Typography>
              </CardHeader>

              <CardContent>
                {defaultAssembly ? (
                  <Grid item>
                    <AssemblyName
                      assemblyName={defaultAssembly?.name}
                      assemblyId={defaultAssembly?.id}
                    />
                  </Grid>
                ) : (
                  <Grid item>
                    <Alert severity="warning">
                      {lang.defaultAssemblyAssign}
                    </Alert>
                  </Grid>
                )}

                <Grid item>
                  <FlexBox>
                    {props.options?.canAddAssembly && !defaultAssembly && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenAssemblyListModal(true)}>
                        {lang.addAssembly}
                      </Button>
                    )}

                    {props.options?.canEditAssembly &&
                      defaultAssembly != null && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AutoreNewIcon />}
                          sx={{ mr: 1 }}
                          onClick={() => setOpenAssemblyListModal(true)}>
                          {lang.replace}
                        </Button>
                      )}
                    {props.options?.canScheduleAssembly &&
                      defaultAssembly != null && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CalendarIcon />}
                          disabled={!!scheduleAssembly}
                          onClick={() => {
                            setScheduleAssemblySelected(true);
                            setOpenAssemblyListModal(true);
                          }}>
                          {lang.schedule}
                        </Button>
                      )}
                  </FlexBox>
                </Grid>
              </CardContent>
            </AssemblyCard>
          </Grid>
          {!!scheduleAssembly && (
            <Grid item xs={6} md={6} ml={3}>
              <AssemblyCard>
                <CardHeader>
                  <Grid container alignItems="center" style={{ width: '100%' }}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {lang.scheduledAssembly}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {lang.scheduledAssemblyDate}
                      </Typography>
                    </Grid>
                    <Grid item xs={3} />
                  </Grid>
                </CardHeader>
                <CardContent sx={{ display: 'block' }}>
                  <Grid container alignItems="center">
                    <Grid item xs={6}>
                      <AssemblyName
                        assemblyName={scheduleAssembly?.name}
                        assemblyId={scheduleAssembly?.id}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2">
                        {scheduleAssembly?.startDate
                          ? dayjs(scheduleAssembly.startDate).format(
                              'MM/DD/YYYY',
                            )
                          : ''}
                      </Typography>
                    </Grid>
                    {props.options?.canScheduleAssembly && (
                      <Grid item xs={3} container justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            flex: 'none',
                            width: 'auto',
                            justifyContent: 'center',
                          }}
                          startIcon={<DeleteOutline />}
                          onClick={handleUnschedule}>
                          {lang.unschedule}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </AssemblyCard>
            </Grid>
          )}
        </TimeRowGrid>
      </Grid>

      {canViewLocationGroupDaypart && (
        <Grid item xs={12} md={10}>
          <Grid
            container
            spacing={2}
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between">
            {daypartActive &&
              scheduledDayparts.map((day, index) => (
                <Grid item key={index}>
                  <Card
                    $hasDayActive={
                      !isViewOnly &&
                      !props.isDefaultLocationGroup &&
                      !props.rbStoreIntegration &&
                      day.start_time
                    }>
                    <CustomTooltip
                      title={lang.tooltipText}
                      placement="top"
                      arrow
                      isHidden={!(canViewDaypart && !day.start_time)}
                      skipValidateOverflow={true}>
                      <CardHeader
                        $hasDayActive={canViewDaypart && day.start_time}
                        onClick={() => {}}>
                        <Typography variant="h6" component="h3">
                          {day.weekday.name}
                        </Typography>
                        {canViewDaypart && (
                          <Checkbox
                            checked={!!day.start_time}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              toggleDayActiveStatus(
                                event.target.checked,
                                day.weekday_daypart_id,
                              )
                            }
                          />
                        )}
                      </CardHeader>
                    </CustomTooltip>

                    {day.start_time && (
                      <StartTimeContainer>
                        <LabelText>{lang.startTime}</LabelText>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <StyledMobileTimePicker
                            value={dayjs(day.start_time, 'HH:mm:ss')}
                            isOverride={day.isOverride}
                            readOnly={
                              props.isDefaultLocationGroup &&
                              props.rbStoreIntegration
                            }
                            disabled={
                              isViewOnly ||
                              props.isDefaultLocationGroup ||
                              props.rbStoreIntegration
                            }
                            onChange={(time: {
                              format: (arg0: string) => any;
                            }) => {
                              const timeString = time
                                ? time.format('HH:mm:ss')
                                : '';

                              updateStartTime(
                                timeString,
                                day.weekday_daypart_id,
                              );
                            }}
                            onAccept={() =>
                              OOLocationEditTimingsEventHandler(day)
                            }
                            orientation="landscape"
                            slotProps={{
                              textField: {
                                InputProps: {
                                  endAdornment: !isViewOnly && (
                                    <InputAdornment position="end">
                                      <AccessTimeIcon />
                                    </InputAdornment>
                                  ),
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </StartTimeContainer>
                    )}
                  </Card>
                  {hasConflictStartTime(
                    scheduleWeekdayDaypartsRecord,
                    day.weekday.id,
                    day.weekday_daypart_id,
                    day.start_time as string,
                    props.isLocationGroupPage,
                    props.groupDaypartsActiveStatus,
                  ) && (
                    <CustomTooltip
                      title={lang.warningTooltipText}
                      placement="bottom"
                      arrow
                      skipValidateOverflow={true}>
                      <WarningIconContainer>
                        <StyledWarningIcon />
                      </WarningIconContainer>
                    </CustomTooltip>
                  )}
                </Grid>
              ))}
          </Grid>
        </Grid>
      )}

      {openAssemblyListModal && (
        <AssemblyListModal
          open={openAssemblyListModal}
          onClose={() => {
            setScheduleAssemblySelected(false);
            setOpenAssemblyListModal(false);
          }}
          onSelectedAssembly={onSelectedAssembly}
          layoutTypeId={props.locationGroup?.layout_type_id}
          channelName={props.locationGroup?.channel_name}
          excludeAssemblyIds={[
            ...(defaultAssembly?.id ? [defaultAssembly.id] : []),
            ...(scheduleAssembly?.id ? [scheduleAssembly.id] : []),
          ]}
          message={lang.defaultAssemblyInfo}
          title={getAssemblyModalTitle()}
          scheduleAssemblySelected={scheduleAssemblySelected}
        />
      )}
    </>
  );
};
